import { qs, qsa, remove, add } from "@fluorescent/dom";

import { section } from "@/glow";
import { on, emit } from "@/glow/events";
import navigationDots from "@/lib/navigation-dots";
import getMediaQuery from "@/lib/media-queries";
import {
  FadeUpReveal,
  FadeScaleReveal,
  FadeUpSplitReveal,
  shouldAnimate,
} from "@/lib/animation";
import { prefersReducedMotion } from "@/lib/a11y";

const selectors = {
  slider: "[data-slider]",
  slide: ".slideshow__cell",
};

section("slideshow", {
  events: [],
  slideshow: null,
  dotNavigation: null,

  onLoad() {
    const slideshowShouldAnimate = shouldAnimate(this.container);
    const slider = qs(selectors.slider, this.container);
    const slides = qsa(selectors.slide, this.container);
    const { autoplay } = slider.dataset;

    remove(slider, "is-hidden");
    // trigger redraw for transition
    slider.offsetHeight;

    // Animate the first slide only when it is scrolled into view

    const margin = window.matchMedia(getMediaQuery("not-small")).matches
      ? 200
      : 100;
    const threshold = Math.min(margin / this.container.offsetHeight, 0.5);
    const observer = new IntersectionObserver(
      ([{ intersectionRatio: visible }]) => {
        if (visible) {
          this._animateSlide(slides[0]);
          this._unpause();
          observer.disconnect();
        }
      },
      { threshold: threshold }
    );

    if (slides.length > 1) {
      import(flu.chunks.flickity).then(({ Flickity }) => {
        this.slideshow = new Flickity(slider, {
          adaptiveHeight: true,
          autoPlay: Number(autoplay),
          draggable: true,
          prevNextButtons: false,
          wrapAround: true,
          pageDots: false,
          dragThreshold: 5,
          pauseAutoPlayOnHover: !window.Shopify.designMode,
          on: {
            ready: () => {
              if (slideshowShouldAnimate) {
                observer.observe(this.container);
              }
            },
          },
        });

        // Pause player from playing while offscreen allowing text animation
        // when player intersects
        this._pause();

        this.dotNavigation = navigationDots(this.container, this.slideshow);

        this.slideshow.on("change", index => {
          const activeSlide = this.slideshow.cells[index].element;
          this._animateSlide(activeSlide);
          this.dotNavigation && this.dotNavigation.update(index);
        });

        emit("slideshow:initialized");
      });
    } else if (slides.length) {
      add(slides[0], "is-selected");

      if (shouldAnimate(this.container)) {
        observer.observe(this.container);
      }
    }
  },

  _pause() {
    this.slideshow && this.slideshow.pausePlayer();
  },

  _unpause() {
    this.slideshow && this.slideshow.unpausePlayer();
  },

  _handleBlockSelect(slideIndex) {
    this.slideshow.select(slideIndex);
    this._pause();
  },

  _animateSlide(target) {
    if (!prefersReducedMotion()) {
      const image = qs(".image, .placeholder-image", target);
      const mobileImage = qs(".image.slideshow__image--mobile", target);
      const fadeUpSplitItems = qsa(".animation-fade-up-split-reveal", target);
      const fadeUpItems = qsa(".animation-fade-up-reveal", target);
      const reveals = [];
      const imageReveal = new FadeScaleReveal(image, {
        scaleStart: 1.25,
      });
      let mobileImageReveal = null;
      if (mobileImage) {
        mobileImageReveal = new FadeScaleReveal(mobileImage, {
          scaleStart: 1.25,
        });
      }

      fadeUpSplitItems.forEach(item => {
        reveals.push(new FadeUpSplitReveal(item));
      });

      fadeUpItems.forEach(item => {
        reveals.push(new FadeUpReveal(item));
      });

      if (
        !window.matchMedia(getMediaQuery("not-small")).matches &&
        mobileImage
      ) {
        mobileImageReveal.play(0);
      } else {
        imageReveal.play(0);
      }

      let delay = 0;
      reveals.forEach(reveal => {
        reveal.play(delay);

        if (reveal.type === "FadeUpSplitReveal") {
          delay += reveal.lineCount * window.theme.animation.delay;
        } else {
          delay += window.theme.animation.delay;
        }
      });
    }
  },

  onBlockSelect({ target }) {
    if (this.slideshow) {
      this._handleBlockSelect(target.dataset.index);
    } else {
      // Listen for initalization if slideshow does not exist
      this.events.push(
        on("slideshow:initialized", () => {
          this._handleBlockSelect(target.dataset.index);
        })
      );
    }
  },

  onBlockDeselect() {
    if (this.slideshow) {
      this._unpause();
    } else {
      // Listen for initalization if slideshow does not exist
      this.events.push(
        on("slideshow:initialized", () => {
          this._unpause();
        })
      );
    }
  },

  onUnload() {
    this.slideshow && this.slideshow.destroy();
    this.events.forEach(unsubscribe => unsubscribe());
    this.dotNavigation && this.dotNavigation.unload();
    this.observer?.destroy();
  },
});
